/**
 * Typed WebSocket wrapper for signaling.
 *
 * Provides:
 *   - request/response correlation (auto-generates an `id`, returns a Promise
 *     that resolves on the matching response, rejects on error or timeout)
 *   - sendNotification() for one-way messages
 *   - on(type, handler) for server → client notifications
 *
 * Phase 2: ready to use; Phase 3 (T048 RoomClient.join) is the first consumer.
 */
import {
  parseMessage,
  type Message,
  type Notification,
  type ProtocolError,
  type Request,
} from '@webrtc/protocol'

/** Window in which a request must receive its response, otherwise rejects. */
const REQUEST_TIMEOUT_MS = 15_000

/** Narrow Request to its `type` and the corresponding `data` shape. */
export type RequestOf<T extends Request['type']> = Extract<Request, { type: T }>
export type RequestData<T extends Request['type']> = RequestOf<T>['data']

/** Subset of Notification per its `type`. */
export type NotificationOf<T extends Notification['type']> = Extract<Notification, { type: T }>
export type NotificationData<T extends Notification['type']> = NotificationOf<T>['data']

export class SignalingError extends Error {
  constructor(public readonly error: ProtocolError) {
    super(`${error.code}: ${error.message}`)
    this.name = 'SignalingError'
  }
}

interface PendingRequest {
  resolve: (data: unknown) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

export type NotificationHandler<T extends Notification['type']> = (
  data: NotificationData<T>,
) => void

/** Type-erased shape used inside the handlers map for storage uniformity. */
type AnyHandler = (data: unknown) => void

export class SignalingClient {
  private ws: WebSocket | null = null
  private nextId = 1
  private readonly pending = new Map<string, PendingRequest>()
  private readonly handlers = new Map<string, Set<AnyHandler>>()
  private onCloseCb: ((code: number, reason: string) => void) | null = null
  /**
   * Buffer for notifications that arrive before any handler is registered for
   * their type. Flushed (and cleared per-type) the first time `on(type, …)` is
   * called. Bounded implicitly by the number of notification types the protocol
   * defines — in practice at most a handful per type during the join window.
   */
  private readonly pendingNotifications = new Map<string, unknown[]>()

  constructor(private readonly url: string) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url)
      this.ws = ws

      ws.addEventListener('open', () => resolve(), { once: true })

      ws.addEventListener('error', () => reject(new Error(`Failed to connect to ${this.url}`)), {
        once: true,
      })

      ws.addEventListener('message', (ev: MessageEvent<string>) => {
        const msg: Message | null = parseMessage(ev.data)
        if (!msg) {
          // Server promised valid JSON per protocol; this is exceptional.
          console.error('signaling: dropped unparseable message', ev.data.slice(0, 200))
          return
        }
        this.dispatch(msg)
      })

      ws.addEventListener('close', (ev) => {
        // Fail any in-flight requests so callers don't hang forever.
        for (const [, p] of this.pending) {
          clearTimeout(p.timer)
          p.reject(new Error(`WebSocket closed (${ev.code})`))
        }
        this.pending.clear()
        this.onCloseCb?.(ev.code, ev.reason)
      })
    })
  }

  /** Set a single onClose handler — replaces any previous one. */
  onClose(cb: (code: number, reason: string) => void) {
    this.onCloseCb = cb
  }

  /** Send a request and await its typed response. */
  request<T extends Request['type']>(type: T, data: RequestData<T>): Promise<unknown> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('signaling: not connected'))
    }

    const id = `req-${this.nextId++}`

    return new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id)
        reject(new Error(`signaling: request "${type}" timed out after ${REQUEST_TIMEOUT_MS}ms`))
      }, REQUEST_TIMEOUT_MS)

      this.pending.set(id, { resolve, reject, timer })

      const envelope = { kind: 'request' as const, id, type, data } as Request
      this.ws!.send(JSON.stringify(envelope))
    })
  }

  /** Fire-and-forget client → server notification. */
  sendNotification<T extends Notification['type']>(type: T, data: NotificationData<T>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('signaling: not connected')
    }
    const envelope = { kind: 'notification' as const, type, data } as Notification
    this.ws.send(JSON.stringify(envelope))
  }

  /** Subscribe to a server → client notification type. Returns unsubscribe. */
  on<T extends Notification['type']>(type: T, handler: NotificationHandler<T>): () => void {
    let set = this.handlers.get(type)
    if (!set) {
      set = new Set()
      this.handlers.set(type, set)
    }
    const erased = handler as AnyHandler
    set.add(erased)

    // Flush any notifications that arrived before this handler was registered.
    const buffered = this.pendingNotifications.get(type)
    if (buffered && buffered.length > 0) {
      this.pendingNotifications.delete(type)
      for (const data of buffered) {
        try {
          erased(data)
        } catch (err) {
          console.error(`signaling: buffered handler for ${type} threw`, err)
        }
      }
    }

    return () => {
      set!.delete(erased)
    }
  }

  close() {
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) {
      this.ws.close(1000, 'client leave')
    }
    this.ws = null
  }

  private dispatch(msg: Message) {
    if (msg.kind === 'response') {
      const pending = this.pending.get(msg.id)
      if (!pending) {
        // Late response (timed out or unknown id). Silently drop.
        return
      }
      this.pending.delete(msg.id)
      clearTimeout(pending.timer)
      if (msg.ok) {
        pending.resolve(msg.data)
      } else {
        pending.reject(new SignalingError(msg.error))
      }
      return
    }

    if (msg.kind === 'notification') {
      const set = this.handlers.get(msg.type)
      if (!set || set.size === 0) {
        // No handler registered yet — buffer the notification so it can be
        // replayed when the first on(type, …) subscription arrives.
        let buf = this.pendingNotifications.get(msg.type)
        if (!buf) {
          buf = []
          this.pendingNotifications.set(msg.type, buf)
        }
        buf.push(msg.data)
        return
      }
      for (const handler of set) {
        try {
          handler(msg.data)
        } catch (err) {
          console.error(`signaling: handler for ${msg.type} threw`, err)
        }
      }
      return
    }

    // Server-initiated requests are not part of MVP protocol; if one shows
    // up it's a bug on the server. Log and ignore.
    console.warn('signaling: unexpected request from server', msg)
  }
}
